/* eslint-disable max-lines-per-function */

import cx from 'classnames';
import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {useRouteMatch} from 'react-router-dom';
import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {
  navigateToRoute,
  navigateToRoute as navigateToRouteCreateFabric,
  useCurrentRoute,
  useCurrentRoute as useCurrentRouteCreateFabric,
} from '~/CreateFabricInvitation/navigation';
import {ConfirmLeaveModalContainer} from '~/CreateFabricInvitation/navigation/ConfirmLeaveModal';
import {eventStepCount} from '~/CreateFabricInvitation/navigation/createSteps';
import {eventIsPremium} from '~/CreateFabricInvitation/reducers/details/selectors';
import {
  selectCurrentSection,
  selectPristineBackground,
  selectPristineEnvelopeDesign,
  selectUsedPremiumFeature,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {selectShowLeaveModal} from '~/CreateFabricInvitation/reducers/modals';
import {setShowLeaveModal} from '~/CreateFabricInvitation/reducers/modals/actions';
import {isInStep4RefreshVariation} from '~/CreateFabricInvitation/utils/experiments';
import {InjectableMegaNav} from '~/common/components/MegaNav/InjectableMegaNav';
import {ExitPremiumPreviewModal} from '~/common/FabricInvitation/EventCreateDesign/components/ExitPremiumPreviewModal/ExitPremiumPreviewModal';
import {useNavigator} from '~/common/FabricInvitation/EventCreateDesign/components/NavBottom/hooks/useNavigator';
import {Sections} from '~/common/FabricInvitation/EventCreateDesign/constants';
import {
  getEventStepsType,
  isInFreeAddGuestsStepExpVariant,
} from '~/common/fabric/EventGuests/components/DisplayCondition/addGuestsCondition';
import {isInCreateFlowNavABTest} from '~/common/fabric/experiments';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {selectIsDetailsPristine} from '~/common/fabric/reducers/details/selectors';
import {getEventId} from '~/common/fabric/reducers/eviteEvent/selectors';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {selectPristine} from '~/common/fabric/reducers/fabricDesign/selectors';
import {UpsellModal} from '~/common/fabric/UpsellModal/UpsellModal';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {SimpleNav} from '~library/organisms/SimpleNav';
import {HeaderNavigation} from './components/HeaderNavigation';
import {NamedStepsNavWrapper} from './components/NamedStepsNavWrapper';
import {PremiumPreviewNav} from './components/PremiumPreviewNav';
import {useHeaderModals} from './hooks/useHeaderModals';
import {ProgressBar} from './ProgressBar';

import './NavHeader.sass';

export const NavHeader = () => {
  const isDesktop = useIsLargishAndUp();
  const inStep4RefreshVariation = isInStep4RefreshVariation(isDesktop);
  const currentRoute = useCurrentRoute();
  const store = useStore();
  const showLeaveModal = useSelector(selectShowLeaveModal);
  const isPremium = useSelector(eventIsPremium);
  const usedPremiumFeature = useSelector(selectUsedPremiumFeature);
  const isDetailsPristine = useSelector(selectIsDetailsPristine);
  const isDesignPristine = useSelector(selectPristine);
  const isUyoPristine = useSelector(selectPristineEnvelopeDesign);
  const isBackgroundPristine = useSelector(selectPristineBackground);
  const currentSection = useSelector(selectCurrentSection);
  const eventId = useSelector(getEventId);
  const eventStepsType = useSelector(getEventStepsType);
  const dispatch = useDispatch();

  const isPristine = isDesignPristine && isBackgroundPristine && isUyoPristine && isDetailsPristine;
  const hasBeenSaved = !!eventId;
  const showSteps = currentRoute.meta.countSteps;
  const isGuestStep = useRouteMatch('/:eventId/guests')?.isExact;
  const isFullPageGuestOptions = useRouteMatch('/:eventId/guest-options')?.isExact;
  const totalSteps = eventStepCount[eventStepsType];

  const navigate = useNavigator({
    isMobile: !isDesktop,
    disableAutoOpenEditor: currentSection === Sections.CARD_DESIGN,
  });

  const {
    showUpsellModal,
    upsellModalMode,
    showExitPremiumPreviewModal,
    setShowUpsellModal,
    setShowExitPremiumPreviewModal,
    toggleUpsellModal,
    downgradeIfDismissCheckout,
    onClosePremiumPreviewClick,
    upgradeToPremium,
    handleExitPremiumPreview,
    handleShowLeaveModal,
  } = useHeaderModals({store, navigate, currentRoute, isPremium});

  const stepLabel = useMemo(() => {
    if (!showSteps) return '';
    // AB TEST: 'test_add-guests-step-free' - TO ROLL BACK: Remove this conditional and just use the else logic
    if (isInFreeAddGuestsStepExpVariant(store.getState())) {
      return `Step ${currentRoute.id}:`;
    }
    return `${currentRoute.id} of ${totalSteps}:`;
  }, [currentRoute.id, showSteps, store, totalSteps]);

  const simpleNavObj = {
    prev: {
      onClick: () => {
        store.dispatch({
          type: UPDATE_EVENT_DETAILS,
          payload: {preventUpdateModal: true},
        });
        navigateToRoute('back', store);
      },
      text: 'Back',
      qaId: 'upsell-back',
      buttonVariant: 'secondary',
    },
    next: {
      onClick: () => {
        store.dispatch({
          type: UPDATE_EVENT_DETAILS,
          payload: {preventUpdateModal: true},
        });
        navigateToRoute('next', store);
      },
      text: 'Skip',
      qaId: 'upsell-next',
      buttonVariant: 'secondary',
    },
  };

  const onSave = useCallback(() => store.dispatch(saveEventThunk()), [store.dispatch]);
  const isInNavABTest = isInCreateFlowNavABTest();
  const showPreviewNav =
    (currentRoute.id === 1 || (currentRoute.id === 4 && isInNavABTest)) &&
    !isPremium &&
    usedPremiumFeature;
  const showProgressBar =
    !showPreviewNav && !isInNavABTest && (!inStep4RefreshVariation || !currentRoute.meta.editing);
  const premiumPreview = usedPremiumFeature && !isPremium;
  const isCheckoutStep = currentRoute.name === 'review';

  const getNavToShow = useMemo(() => {
    const showPremiumPreviewNav = showPreviewNav && (
      <PremiumPreviewNav
        isLargishAndUp={isDesktop}
        onClosePremiumPreviewClick={onClosePremiumPreviewClick}
        toggleUpsellModal={toggleUpsellModal}
      />
    );

    const navAbInCreateFlow = isInNavABTest && !currentRoute.meta.editing;

    if (navAbInCreateFlow) {
      return (
        <>
          <NamedStepsNavWrapper
            currentRoute={currentRoute}
            isLargishAndUp={isDesktop}
            isPremium={isPremium}
            showSteps={showSteps}
            stepLabel={stepLabel}
            isGuestStep={isGuestStep}
            eventId={eventId}
            totalSteps={totalSteps}
            handleShowLeaveModal={handleShowLeaveModal}
            onSave={onSave}
            premiumPreview={premiumPreview}
            store={store}
            onClosePremiumPreviewClick={onClosePremiumPreviewClick}
            toggleUpsellModal={toggleUpsellModal}
            navigateToRouteHook={navigateToRouteCreateFabric}
            useCurrentRouteHook={useCurrentRouteCreateFabric}
          />
          {showPremiumPreviewNav}
        </>
      );
    }

    return showPreviewNav ? (
      showPremiumPreviewNav
    ) : (
      <HeaderNavigation
        currentRoute={currentRoute}
        isLargishAndUp={isDesktop}
        isPremium={isPremium}
        showSteps={showSteps}
        stepLabel={stepLabel}
        isGuestStep={isGuestStep}
        eventId={eventId}
        totalSteps={totalSteps}
        handleShowLeaveModal={handleShowLeaveModal}
        onSave={onSave}
        premiumPreview={premiumPreview}
        store={store}
      />
    );
  }, [
    showPreviewNav,
    isDesktop,
    onClosePremiumPreviewClick,
    toggleUpsellModal,
    currentRoute,
    isPremium,
    showSteps,
    stepLabel,
    isGuestStep,
    eventId,
    totalSteps,
    handleShowLeaveModal,
    onSave,
    store,
    premiumPreview,
    isInNavABTest,
  ]);

  if (currentRoute.showMegaNav) {
    return <InjectableMegaNav />;
  }

  if (currentRoute.showSimpleNav) {
    return <SimpleNav prev={simpleNavObj.prev} next={simpleNavObj.next} />;
  }

  if (isFullPageGuestOptions) return null;

  return (
    <>
      <div
        className={cx('fabric-header__wrapper', {
          'no-progress-bar': !showProgressBar,
          'fabric-header__wrapper--checkout': isInNavABTest && isCheckoutStep,
        })}
      >
        <header
          className={cx('fabric-header', {
            frp: currentRoute.id > totalSteps,
            'premium-preview-header': showPreviewNav,
            'premium-preview-header--named-steps-nav': showPreviewNav && isInNavABTest,
          })}
        >
          {getNavToShow}
        </header>
        {showProgressBar && <ProgressBar currentStepId={currentRoute.id} totalSteps={totalSteps} />}
      </div>

      {showLeaveModal && (
        <ConfirmLeaveModalContainer
          isFirstStep={!hasBeenSaved}
          isEditingFlow={currentRoute.meta.editing}
          isPristine={isPristine}
          leave={async () => {
            store.dispatch(setShowLeaveModal(false));
            downgradeIfDismissCheckout();
            await navigateToRoute('exit', store, true);
          }}
          saveAndLeave={async () => {
            const force = currentRoute.meta.creating ? 'saveAndExit' : false;
            store.dispatch(setShowLeaveModal(false));
            await navigateToRoute('exit', store, force);
          }}
          cancelLeave={() => store.dispatch(setShowLeaveModal(false))}
          isGuestStep={isGuestStep}
        />
      )}

      {showUpsellModal && (
        <UpsellModal
          noAutoRestore
          toggleUpsellModal={() => toggleUpsellModal(!showUpsellModal)}
          closeOverlay={() => {
            setShowUpsellModal(false);
            setShowExitPremiumPreviewModal(false);
            dispatch({
              type: UPDATE_DESIGN_OPTIONS,
              payload: {editingMobile: false},
            });
          }}
          onConfirmAction={upgradeToPremium}
          mode={upsellModalMode}
          onDenyAction={handleExitPremiumPreview}
        />
      )}

      {showExitPremiumPreviewModal && (
        <ExitPremiumPreviewModal
          closeOverlay={() => {
            setShowExitPremiumPreviewModal(false);
            dispatch({
              type: UPDATE_DESIGN_OPTIONS,
              payload: {editingMobile: false},
            });
          }}
          onConfirmAction={() => {
            handleExitPremiumPreview();
            dispatch({
              type: UPDATE_DESIGN_OPTIONS,
              payload: {editingMobile: false},
            });
          }}
        />
      )}
    </>
  );
};
